// src/encounter/lib/narrative-line-wire.ts
//
// PROD-3043 · frame v2 only (design desk D-73, 6 Oct, and its amendment after the cross-model review).
// A stored narrative that is a Quill PAIR on the wire — the A&P narrative (`PatientAssessment.notes`
// Delta + `notePlainText`) — edited as an inline note LINE (plain text with pieces, `note-line-delta.ts`)
// instead of the "Add Text Notes" dialog.
//
// THE WIRE DOES NOT CHANGE (D-73). Reading: the line's text is the stored plain text (the half every
// reader already shows); the Delta's pieces are kept as pieces. Writing: the same pair — a Delta that
// ends in "\n" (what Flutter's flutter_quill reads) and its flattened text — so a note typed inline
// renders in the Flutter PA.
//
// THE LOSSLESS RULE (D-73 amendment). The line edits a narrative only when it holds plain text and
// phrase pieces the line keeps as pieces. Anything else is read-only here, and the host offers today's
// dialog to edit it:
//   · formatting (bold, a list, a colour, a header, an alignment) or an image in the stored Delta;
//   · a piece the line cannot carry as a piece (a chart quote in the A&P's own wording, an unreadable run);
//   · a chart quote pulled for another patient or visit, or one that cannot be checked yet (contract G5 —
//     the dialog removes it, with a notice; the line never re-saves it as this patient's coded value);
//   · a Delta that no longer derives the stored text (the two halves disagree).
// So the line never rewrites what it cannot write back, and a narrative that is not edited round-trips
// unchanged.
import { plainTextToDeltaJson } from '@/dashboard/quill-delta';
import { deltaPlainText, parseNoteOps, type DeltaOp } from './note-delta';
import { deltaToSegs, segsHavePieces, segsText, segsToDelta, type LineSeg } from './note-line-delta';
import { PIECE_ATTR, PIECE_EMBED, chartOrigins, type NoteWhere } from './note-pieces';

/** The stored plain text as a line reads it: terminal newlines are Quill's document end, not text. */
function lineText(plain: string | null | undefined, ops: readonly DeltaOp[]): string {
  const text = plain?.trim() ? plain : deltaPlainText([...ops]);
  return text.replace(/\n+$/, '');
}

/** The number of pieces in WIRE ops (consecutive runs sharing one `cps-piece` value are one piece). */
function wirePieceCount(ops: readonly DeltaOp[]): number {
  let n = 0;
  let prev: unknown;
  for (const op of ops) {
    const raw = op.attributes?.[PIECE_ATTR];
    if (raw !== undefined && raw !== prev) n++;
    prev = raw;
  }
  return n;
}

/**
 * Does the stored Delta carry anything a plain line cannot keep: a formatting attribute (bold, a list,
 * a colour, a header) or an embed that is not text (an image)? Pieces do not count; the line keeps them.
 */
export function wireHasFormatting(deltaJson: string | null | undefined): boolean {
  return parseNoteOps(deltaJson ?? '').some((op) => {
    if (typeof op.insert !== 'string') return true;
    return Object.keys(op.attributes ?? {}).some((k) => k !== PIECE_ATTR);
  });
}

/** The stored narrative as an inline line: its segments, and whether the line may edit it losslessly. */
export interface InlineNarrative {
  segs: LineSeg[];
  editable: boolean;
}

/** The stored pair → the line (THE LOSSLESS RULE above). `where`: this note's patient and visit (G5). */
export function readInlineNarrative(
  deltaJson: string | null | undefined,
  plain: string | null | undefined,
  where: NoteWhere,
): InlineNarrative {
  const ops = parseNoteOps(deltaJson ?? '');
  const text = lineText(plain, ops);
  const asText: LineSeg[] = text ? [{ text }] : [];
  if (wireHasFormatting(deltaJson)) return { segs: asText, editable: false };
  const pieces = wirePieceCount(ops);
  if (pieces === 0) {
    // Plain: the line rewrites the Delta from the text, so the two halves must still agree. No Delta stored
    // at all (a new narrative, or a legacy row with only notePlainText): nothing to lose, the line writes one.
    if (!deltaJson?.trim()) return { segs: asText, editable: true };
    return { segs: asText, editable: deltaPlainText(ops).replace(/\n+$/, '') === text };
  }
  const segs = deltaToSegs(deltaJson);
  const kept = segs.filter((s) => s.piece);
  const lossless =
    segsText(segs) === text &&
    kept.length === pieces &&
    kept.every((s) => {
      if (s.piece?.t !== 'chart') return true;
      const origin = chartOrigins([{ insert: { [PIECE_EMBED]: s.piece } }], where);
      return origin.elsewhere === 0 && origin.unchecked === 0;
    });
  return lossless ? { segs, editable: true } : { segs: asText, editable: false };
}

/** The line's segments → the stored pair: `plain` for `notePlainText`, `delta` for `notes`. */
export function lineSegsToWire(segs: readonly LineSeg[]): { plain: string; delta: string } {
  const plain = segsText(segs);
  return { plain, delta: segsHavePieces(segs) ? segsToDelta(segs) : plainTextToDeltaJson(plain) };
}

// ── Another writer while the clinician types (inline-note-line.tsx) ─────────────────────────────────

/** The segments covering text [from, to): a piece wholly inside is kept, one cut by either end keeps its text. */
function segsSlice(segs: readonly LineSeg[], from: number, to: number): LineSeg[] {
  const out: LineSeg[] = [];
  let pos = 0;
  for (const seg of segs) {
    const end = pos + seg.text.length;
    const lo = Math.max(from, pos);
    const hi = Math.min(to, end);
    if (hi > lo) out.push(lo === pos && hi === end ? seg : { text: seg.text.slice(lo - pos, hi - pos) });
    pos = end;
  }
  return out;
}

/**
 * Another writer changed the stored line from `base` to `next` while the clinician typed `draft` (ambient
 * appending "Suspected: …"; its Discard taking that line back out): the same change applied to the draft,
 * with the incoming pieces kept. The changed span is placed by ANCHOR, never by a search: at the draft's
 * end when the draft still ends with the stored text after it (ambient writes at the end), else at the
 * draft's start when the draft still starts with the stored text before it. Undefined → it cannot be
 * placed, and the draft is kept as typed.
 *
 * Known limit (cross-model review, round 2): text the clinician typed, without leaving the line, that is
 * identical to the line ambient takes back out and sits exactly where it sat is taken for that line. A
 * Discard is a click in the rail, which ends the typing (blur saves the draft) before it runs.
 */
export function rebaseExternal(draft: readonly LineSeg[], base: string, next: readonly LineSeg[]): LineSeg[] | undefined {
  const theirs = segsText(next);
  const mine = segsText(draft);
  let start = 0;
  while (start < base.length && start < theirs.length && base[start] === theirs[start]) start++;
  let tail = 0;
  while (tail < base.length - start && tail < theirs.length - start && base[base.length - 1 - tail] === theirs[theirs.length - 1 - tail]) tail++;
  // Widen the window to whole incoming pieces, so the diff never cuts a piece (and strips its coding). The
  // text around the window is the same in `base` and `next`, so both narrow by the same amounts.
  let pos = 0;
  for (const seg of next) {
    const end = pos + seg.text.length;
    if (seg.piece) {
      if (pos < start && end > start) start = pos;
      if (pos < theirs.length - tail && end > theirs.length - tail) tail = theirs.length - end;
    }
    pos = end;
  }
  const removed = base.slice(start, base.length - tail);
  const before = base.slice(0, start);
  const after = base.slice(base.length - tail);
  const at = mine.endsWith(removed + after) ? mine.length - after.length - removed.length : mine.startsWith(before + removed) ? start : -1;
  if (at < 0) return undefined;
  return [...segsSlice(draft, 0, at), ...segsSlice(next, start, theirs.length - tail), ...segsSlice(draft, at + removed.length, mine.length)];
}

// ── Whose note a draft is (inline-note-line.tsx, history-notes-line.tsx) ────────────────────────────

/** Two owners name different records (an id each side knows, and they differ). Unknown ids adopt. */
export function otherRecord(a: NoteWhere, b: NoteWhere | undefined): boolean {
  if (!b) return false;
  return (!!a.patientId && !!b.patientId && a.patientId !== b.patientId) || (!!a.encounterId && !!b.encounterId && a.encounterId !== b.encounterId);
}

/** May a draft owned by `a` be written where the line now points (`b`)? Every id the draft knows must be
 *  named, identically, by `b`: a known draft never flushes into a record whose ids are not known yet. */
export function sameRecord(a: NoteWhere, b: NoteWhere | undefined): boolean {
  const t = b ?? {};
  return (!a.patientId || a.patientId === t.patientId) && (!a.encounterId || a.encounterId === t.encounterId);
}
